import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, RefreshCw, Info, Search, List } from "lucide-react";
import { Card, CardContent, Button, AdminAddUser } from "@b2b/ui-components";
import { useAuth } from "../../auth/Authprovider";
import { useUserStats } from "../../hooks/useUserStats";
import { useUserList } from "../../hooks/useUserList";
import { UserStatistics } from "@b2b/ui-components";
import { RecentUserActivity } from "../../components/User/RecentUserActivity";

const UserDashboard = () => {
  console.log("📊 UserDashboard component rendered");
  const {
    isLoading: authLoading,
  } = useAuth();
  const navigate = useNavigate();
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  const { stats, loading: statsLoading, refresh: refreshStats } = useUserStats();
  const { users, loading: usersLoading, refresh: refreshUsers } = useUserList({
    autoFetch: true,
  });

  const handleRefresh = async () => {
    await Promise.all([refreshStats(), refreshUsers()]);
  };

  const isLoading = statsLoading || usersLoading || authLoading;

  return (
    <div className="space-y-8 mt-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            User Management Dashboard
          </h1>
          <p className="text-muted-foreground">
            Overview of all users, statistics, and recent activity
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowAddUserModal(true)}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/admin/users/search")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold">Search Users</h3>
                <p className="text-sm text-muted-foreground">Find users by name, email, or role</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/admin/users/list")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <List className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">View All Users</h3>
                <p className="text-sm text-muted-foreground">Browse and manage all users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowAddUserModal(true)}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <UserPlus className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold">Add New User</h3>
                <p className="text-sm text-muted-foreground">Create a new user account</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      {stats && (
        <UserStatistics statistics={stats} isLoading={statsLoading} />
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Info className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">
                User Management
              </h3>
              <p className="text-sm text-blue-700">
                Manage buyers, sellers, and user permissions. Use the navigation above to search or view all users.
                Approve or reject user registrations, activate or suspend accounts, and manage user roles.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentUserActivity users={users} isLoading={usersLoading} />
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate("/admin/users/search")}
              >
                <Search className="mr-2 h-4 w-4" />
                Search Users
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate("/admin/users/list")}
              >
                <List className="mr-2 h-4 w-4" />
                View All Users
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setShowAddUserModal(true)}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add New User
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <AdminAddUser
        open={showAddUserModal}
        onOpenChange={setShowAddUserModal}
        onUserCreated={handleRefresh}
      />
    </div>
  );
};

export default UserDashboard;

